import type { StatusTone } from "./status";
import type { AnomalyFlag, Session, SessionDetail, SessionPush } from "./types";

/**
 * A charging session, translated for an operator.
 *
 * Pure and apart from the components that render it, because the decisions here
 * are the interesting part: which number may be shown as final, how long a live
 * session has been running, and what a flag on a row means an operator should do.
 * None of that needs a DOM to check.
 */

export interface DisplayEnergy {
  readonly wh: number | null;
  /**
   * Set when the figure came from interim readings rather than from the meter
   * registers (spec 6.3). The caller renders it differently — an estimate that
   * looks like a billable figure is the one mistake this module exists to prevent.
   */
  readonly isEstimate: boolean;
}

/**
 * The energy to put in a session's energy column.
 *
 * A table has one such column, so the two figures the API keeps apart have to
 * share it, and the flag is what stops the sharing from becoming a merge. The
 * committed figure always wins where both exist: falling back to an estimate on a
 * closed session would replace a meter reading with a guess.
 */
export function displayEnergy(row: Session): DisplayEnergy {
  if (row.energyWh !== null) return { wh: row.energyWh, isEstimate: false };
  if (row.inFlightWh !== null) return { wh: row.inFlightWh, isEstimate: true };

  // Neither: a session that has opened and reported nothing. `null` renders as an
  // em dash, which says "not known yet" — unlike `0.0 kWh`, which is a claim.
  return { wh: null, isEstimate: false };
}

/**
 * How long the session ran, by the charge point's own clock.
 *
 * `now` is a parameter rather than read from the clock, so an open session's
 * duration is testable and a closed one is deterministic.
 */
export function sessionDuration(row: Session, now: number): string {
  const start = Date.parse(row.startedAtCp);
  if (Number.isNaN(start)) return "—";

  const end = row.endedAtCp === null ? now : Date.parse(row.endedAtCp);
  if (Number.isNaN(end)) return "—";

  const ms = end - start;
  // Spec 6.5 keeps the station's timestamps verbatim, so a station whose clock
  // stepped backwards mid-session produces a negative span. There is no honest
  // duration to print for it, and inventing one would hide the anomaly the row's
  // `clock_suspect` flag is trying to report.
  if (ms < 0) return "—";

  const minutes = Math.floor(ms / 60_000);
  // Spec 3.6's DeAuthorized pair starts and stops inside the same second. "0m"
  // reads as missing data rather than as a session that barely happened.
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

const STATE_TONES: Readonly<Record<Session["state"], StatusTone>> = {
  active: "charging",
  /** A normal close. Nothing to look at, which is what `idle` says. */
  ended: "idle",
  /**
   * No StopTransaction ever arrived, so the closing register came from the last
   * interim reading. The energy is usable; its provenance is not the same as a
   * normal close, and only this tone says so at a glance.
   */
  abandoned: "faulted",
};

export function sessionStateTone(state: Session["state"]): StatusTone {
  return STATE_TONES[state] ?? "idle";
}

const ANOMALIES: Readonly<Record<AnomalyFlag, string>> = {
  meter_regression:
    "The closing register read lower than the opening one — the meter ran backwards. Energy is recorded as zero rather than negative.",
  clock_suspect:
    "This station's clock was more than 24 hours from ours, so the session counts toward the period the server saw it end in.",
  orphaned_session:
    "No StopTransaction arrived. The session was closed at its last interim reading.",
  late_stop:
    "The StopTransaction arrived after the session had already been closed. The figures were corrected to the station's own numbers.",
  authorization_refused:
    "The card was refused when the station asked to start. The session is kept so the refusal is on record; no energy was delivered.",
  missed_start:
    "The first message seen for this transaction was not its start, so the session opened late, at the register that message carried.",
};

/**
 * An unknown flag is printed verbatim rather than dropped. The server can record
 * one this build has never heard of, and hiding it would remove the only thing on
 * the row saying the data is unusual.
 */
export function explainAnomaly(flag: string): string {
  return (
    (ANOMALIES as Readonly<Record<string, string>>)[flag] ??
    `The server recorded "${flag}", which this panel does not recognise.`
  );
}

/**
 * What the chart is actually showing.
 *
 * Spec 7's curve is a summary written once at close, and the live one is built
 * from samples that will move. A chart with no caption invites reading a session
 * still in progress as a finished one.
 */
export function curveCaption(row: SessionDetail): string {
  if (row.curveSource === "live") {
    return "Built from the readings received so far. This session is still in progress, so the curve will change.";
  }

  if (row.curveSource === "consolidated") {
    const from =
      row.sampleCount === null ? "the session's readings" : `${row.sampleCount} readings`;
    return `Summarised from ${from} when the session closed. It outlives the readings themselves, which are pruned after 90 days.`;
  }

  return "No reading carried Power.Active.Import, so there is no curve to draw. Check the station's MeterValuesSampledData under Configuration.";
}

const ANOMALY_LABELS: Readonly<Record<AnomalyFlag, string>> = {
  meter_regression: "Meter ran backwards",
  clock_suspect: "Station clock off",
  orphaned_session: "No stop received",
  late_stop: "Late stop",
  authorization_refused: "Card refused",
  missed_start: "Start missed",
};

/**
 * A flag in a few words, for a table cell. `explainAnomaly` is the sentence the
 * session panel gives; this is the name the row carries so the operator knows
 * which rows to open. Unknown flags are printed verbatim, for the same reason.
 */
export function anomalyLabel(flag: string): string {
  return (ANOMALY_LABELS as Readonly<Record<string, string>>)[flag] ?? flag;
}

/**
 * The caption under a session's energy figure: what kind of number it is. A
 * refused session is `abandoned` too, but nothing was delivered and no stop went
 * missing, so it gets its own words rather than the abandoned-session ones.
 */
export function outcomeHint(row: Session): string {
  if (displayEnergy(row).isEstimate) return "live estimate, not billable until the station stops";
  if (row.anomalyFlags.includes("authorization_refused")) return "card refused, nothing delivered";
  if (row.state === "abandoned") return "closed at the last interim reading";
  return "committed";
}

/** Watts as kW to one decimal, the unit power is read in everywhere else. */
export function kilowatts(watts: number | null): string {
  return watts === null ? "—" : (watts / 1000).toFixed(1);
}

export interface SocSpan {
  readonly from: number | null;
  readonly to: number | null;
}

/**
 * The state of charge as a span to draw. A missing end stays missing — an open
 * session has no closing SoC yet, and drawing the bar to 100% would invent one.
 * Clamped, because a station reporting 104% still has to fit on the bar.
 */
export function socSpan(row: SessionDetail): SocSpan | null {
  if (row.socStartPct === null && row.socEndPct === null) return null;
  return { from: clampPct(row.socStartPct), to: clampPct(row.socEndPct) };
}

/** The narrowest mark drawn, so a lone end or a 70 → 71 session is still visible. */
const SOC_MIN_WIDTH = 0.5;

/**
 * Where the filled span sits on a 0–100 track. A lone end is a thin mark at that
 * end; the mark is kept inside the track, so a lone 100% is not clipped away.
 */
export function socBarGeometry(span: SocSpan): { readonly left: number; readonly width: number } {
  const from = span.from ?? span.to ?? 0;
  const to = span.to ?? from;
  const width = Math.max(to - from, SOC_MIN_WIDTH);
  return { left: Math.min(from, 100 - width), width };
}

function clampPct(value: number | null): number | null {
  return value === null ? null : Math.min(100, Math.max(0, value));
}

/** Within this, a station's start and ours are the same moment over a network. */
const CLOCK_AGREEMENT_MS = 60_000;

/**
 * How the station's start time compares with when we heard about it, in words.
 *
 * The audit trail is collapsed by default, and this is the line that tells an
 * operator whether opening it is worth their time. `startedAtServer` is when the
 * StartTransaction *arrived*, so the comparison is lopsided: a station that
 * started offline and sent the message on reconnecting is early by hours with a
 * perfectly good clock, while a start stamped *after* we received it can only be
 * a clock running fast. Only the second is suspect. `null` when a timestamp will
 * not parse — a claim either way would be made up.
 */
export function clockAgreement(
  row: SessionDetail,
): { readonly isSuspect: boolean; readonly text: string } | null {
  const station = Date.parse(row.startedAtCp);
  const server = Date.parse(row.startedAtServer);
  if (Number.isNaN(station) || Number.isNaN(server)) return null;

  const skew = station - server;
  if (Math.abs(skew) <= CLOCK_AGREEMENT_MS) return { isSuspect: false, text: "Clocks agree" };

  const minutes = Math.round(Math.abs(skew) / 60_000);
  const span = minutes < 60 ? `${minutes}m` : `${Math.round(minutes / 60)}h`;
  return skew > 0
    ? { isSuspect: true, text: `Station clock ${span} ahead` }
    : { isSuspect: false, text: `Start reported ${span} late` };
}

export interface PushSummary {
  readonly tone: StatusTone;
  readonly label: string;
  /** The partner's record, or the reason — whichever an operator would act on. */
  readonly detail: string | null;
}

/**
 * Whether "Push now" has anything to do: an App never sent this closed session,
 * or a push stuck where nothing retries it. Queued and sent pushes need no hand.
 */
export function canPushNow(row: SessionDetail): boolean {
  if (row.state === "active") return false;
  return row.unsent.length > 0 || row.pushes.some((push) => push.state === "parked");
}

/**
 * One push, as the session panel's side column states it. `Stuck` is the only
 * one that needs a person: a parked push is never retried by itself.
 */
export function pushSummary(push: SessionPush): PushSummary {
  if (push.state === "parked") {
    return { tone: "faulted", label: "Stuck", detail: push.lastMessage };
  }
  if (push.state === "pending") {
    return push.attempts === 0
      ? { tone: "transitional", label: "Queued", detail: null }
      : {
          tone: "transitional",
          label: "Retrying",
          detail: [push.lastMessage, `${push.attempts} attempt${push.attempts === 1 ? "" : "s"}`]
            .filter((part) => part !== null)
            .join(" · "),
        };
  }
  switch (push.lastOutcome) {
    case "ignored":
      return { tone: "idle", label: "Not theirs", detail: push.lastMessage };
    case "skipped":
      return { tone: "idle", label: "Not sent", detail: push.lastMessage };
    default:
      return { tone: "available", label: "Sent", detail: push.recordNo };
  }
}
